import type { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import * as Sentry from '@sentry/react';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { StatusCodes } from 'http-status-codes';
import z from 'zod';

import { authMutex } from './auth-mutex';
import { refreshResponseSchema } from './refresh-response-schema';
import { IGNORED_ENDPOINTS_ON_401 } from './rest-api-constants';
import { getErrorMessage, standardErrorSchema } from './rest-api-error';

import { AuthStoreSlice } from '../../slices/auth';
import {
  triggerErrorModal,
  triggerUnexpectedErrorModal,
} from '../errorModalService';

import { getCurrentLanguage } from '~/contexts/lang/languageContext';
import { GlobalStoreSlice } from '~/slices/global';
import {
  isExpectedBusiness404,
  shouldSkipSentryCapture,
} from '~/utils/sentry-filters';
import { RootState } from '~/utils/store';

const API_PATH = import.meta.env.VITE_API_PATH ?? '';
export const HTTPS_PROTOCOL = import.meta.env.VITE_HTTPS_PROTOCOL;

export const AUTH_API_PATH = import.meta.env.VITE_API_AUTH_PATH ?? '';
export { IGNORED_ENDPOINTS_ON_401 } from './rest-api-constants';

const extraOptionsSchema = z
  .object({ requireAuth: z.boolean().catch(true) })
  .catch({ requireAuth: true });

type BaseQueryCustomOptions = Partial<z.infer<typeof extraOptionsSchema>>;
const DEFAULT_BASE_QUERY_ARGS = { baseUrl: '' };

type BaseQueryArgs = {
  url: string;
  method?: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  baseURLOverride?: string;
  excludeAPIHeaders?: boolean;
  suppressErrorModal?: boolean;
} & Pick<
  AxiosRequestConfig,
  'headers' | 'withCredentials' | 'responseType' | 'onUploadProgress'
>;

const showErrorModalForStandardError = (
  err: AxiosError,
  parsedErrors: z.infer<typeof standardErrorSchema>,
) => {
  // 401s are suppressed unconditionally — login/auth forms handle these
  // inline via RTK Query mutation state (RequestErrorBanner / StatusCodeErrorBanner).
  const isAuthError = err.response?.status === StatusCodes.UNAUTHORIZED;
  if (isAuthError) return;

  // 404s are handled contextually by the calling component (e.g. PermissionGuard
  // shows PermissionDenied, detail pages redirect). A global modal with a raw
  // backend error string is not useful UX for "not found" responses.
  const isNotFound = err.response?.status === StatusCodes.NOT_FOUND;
  if (isNotFound) return;

  const isUnexpectedError =
    parsedErrors.errors.some((errorObj) => errorObj.code === 'unexpected') ||
    err.response?.status === StatusCodes.INTERNAL_SERVER_ERROR;

  if (isUnexpectedError) {
    triggerUnexpectedErrorModal(err);
  } else {
    triggerErrorModal(err);
  }
};

const axiosBaseQuery =
  ({ baseUrl }: { baseUrl: string } = DEFAULT_BASE_QUERY_ARGS): BaseQueryFn<
    BaseQueryArgs &
      Pick<
        AxiosRequestConfig,
        'headers' | 'withCredentials' | 'responseType' | 'onUploadProgress'
      >,
    unknown,
    FetchBaseQueryError,
    BaseQueryCustomOptions
  > =>
  async (
    {
      url,
      method,
      data,
      params,
      baseURLOverride,
      excludeAPIHeaders,
      suppressErrorModal,
      headers,
      withCredentials: withCredentialsArg,
      responseType,
      onUploadProgress,
    },
    _api,
    _extraOptions,
  ) => {
    try {
      const state = _api.getState() as RootState;
      const extraOptions = extraOptionsSchema.parse(_extraOptions);
      const currentLanguage = getCurrentLanguage();

      const withCredentials =
        withCredentialsArg === undefined
          ? extraOptions.requireAuth
          : withCredentialsArg;
      const result = await axios({
        url: baseURLOverride ? baseURLOverride + url : baseUrl + url,
        method,
        data,
        params,
        headers: {
          ...headers,
          ...(!excludeAPIHeaders && { Region: state.auth.user.region }),
          ...(!excludeAPIHeaders && {
            'X-Frontend-Auth': import.meta.env.VITE_X_FRONTEND_AUTH,
          }),
          ...(!excludeAPIHeaders && {
            'Accept-Confide-Language': currentLanguage.code,
          }),
        },
        withCredentials,
        responseType,
        onUploadProgress,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      const standardErrorParse = standardErrorSchema.safeParse(
        err.response?.data,
      );
      if (standardErrorParse.success) {
        if (!suppressErrorModal) {
          showErrorModalForStandardError(err, standardErrorParse.data);
        }

        return {
          error: {
            status: err.response?.status ?? StatusCodes.BAD_REQUEST,
            data: standardErrorParse.data,
          } as FetchBaseQueryError,
        };
      }
      return {
        error: {
          status: err.response?.status ?? StatusCodes.BAD_REQUEST,
          data: err.response?.data || err.message,
        } as FetchBaseQueryError,
      };
    }
  };

const axiosBaseQueryWithReauth: BaseQueryFn<
  BaseQueryArgs & Pick<AxiosRequestConfig, 'headers' | 'withCredentials'>,
  unknown,
  FetchBaseQueryError,
  BaseQueryCustomOptions
> = async (args, api, extraOptions) => {
  const baseQuery = axiosBaseQuery({
    baseUrl: `${HTTPS_PROTOCOL}${API_PATH}`,
  });
  let result = await baseQuery(args, api, extraOptions ?? {});

  /**
   * Since we we only want to re-authenticate on 401 errors on endpoints after authentication,
   * we need to check if the endpoint we are requesting
   * is in the list of ignored endpoints.
   */
  const isEndpointIgnored = IGNORED_ENDPOINTS_ON_401.includes(args.url);

  if (result.error?.status === 401 && !isEndpointIgnored) {
    if (authMutex.isLocked()) {
      // wait until the mutex is available without locking it
      await authMutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    } else {
      const release = await authMutex.acquire();
      try {
        // try to get a new token
        const refreshResult = await baseQuery(
          {
            url: '/token/refresh',
            method: 'POST',
            data: { is_http_cookie_only: true },
          },
          api,
          {},
        );
        const refreshedCredentials = refreshResponseSchema.safeParse(
          refreshResult.data,
        );

        if (refreshResult.data && refreshedCredentials.success) {
          const data = refreshedCredentials.data;
          api.dispatch(
            AuthStoreSlice.setCredentials({
              accessTokenExpiration: data.access_expiration,
              refreshTokenExpiration: data.refresh_expiration,
              type: data.user_type,
            }),
          );
          // retry the initial query
          result = await baseQuery(args, api, extraOptions ?? {});
        } else {
          const location = window.location;
          const isSecurePortal = location.pathname.includes(
            '/portal/request-for-information',
          );
          if (!isSecurePortal) {
            if (location.pathname.startsWith('/customer')) {
              api.dispatch(
                GlobalStoreSlice.setCustomerUrlCallback(location.pathname),
              );
            }
            // redirect to the logout page
            window.location.href = '/logout';
          }
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    }
  }
  // catch errors that are not 401
  if (result.error && result.error.status !== 401) {
    const errorMessage = getErrorMessage(result.error, undefined, {
      forSentry: true,
    });

    // Only capture unexpected errors to Sentry (skip expected business-logic errors
    // and known 404s that are surfaced contextually by the UI).
    const isExpected404 = isExpectedBusiness404(result.error.status, args.url);
    if (!isExpected404 && !shouldSkipSentryCapture(errorMessage)) {
      // will log when query mutation request fails
      Sentry.captureException(new Error(errorMessage), {
        level: 'warning',
        fingerprint: [
          args.url,
          args.method ?? 'GET',
          String(result.error.status),
        ],
        contexts: {
          request: {
            url: args.url,
            method: args.method,
          },
        },
      });
    }
  }
  return result;
};

const TAG_TYPES = [
  'Tenant',
  'Users',
  'Reports',
  'Schedules',
  'Partner',
  'Localization',
  'Entity',
  'Registers',
] as const;
export const restAPI = createApi({
  reducerPath: 'api',
  baseQuery: axiosBaseQueryWithReauth,
  endpoints: () => ({}),
  tagTypes: TAG_TYPES,
});

export {
  defaultMutationResponseSchema,
  isMutationSuccess,
  paginatedParamsSchema,
  paginationMetaSchema,
  parseOrReturnError,
  parsePaginatedResponse,
  useWrappedMutation,
} from './rest-api-shared';
export type {
  DefaultMutationResponse,
  PaginatedResponse,
} from './rest-api-shared';
export { toParams } from './rest-api-shared';
